'use client';

import IconChevronDown from '@mnd-frontend/icons/components/ChevronDown';
import { useId } from 'react';
import { InputWrapper } from '../Input';
import { Spinner } from '../Spinner';
import { type MultiProps, MultiSelect } from './MultiSelect';
import { type SingleProps, SingleSelect } from './SingleSelect';

type CommonSelectProps = {
  id?: string;
  name?: string;
  label?: string;
  disabled?: boolean;
  skipMargin?: boolean;
  error?: string | null;
  helperText?: string;
  loading?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  'data-testid'?: string;
};

export type SelectProps<T> = CommonSelectProps & (MultiProps<T> | SingleProps<T>);

export const Select = <T,>(props: SelectProps<T>) => {
  const labelId = useId();

  const hasContent = props.isMulti
    ? props.value.length > 0
    : props.options.some(option =>
        'options' in option
          ? option.options.some(o => o.value === props.value)
          : option.value === props.value,
      );
  const disabled = props.disabled || props.loading;

  return (
    <InputWrapper
      disabled={disabled}
      label={props.label}
      skipMargin={props.skipMargin}
      hasContent={hasContent}
      helperText={props.helperText}
      error={props.error}
      trailingIcon={props.loading ? <Spinner size="xs" /> : <IconChevronDown />}
      labelId={labelId}
    >
      {props.isMulti ? (
        <MultiSelect
          {...(props as MultiProps<T>)}
          disabled={disabled}
          hasContent={hasContent}
          labelId={labelId}
          onOpenChange={props.onOpenChange}
        />
      ) : (
        <SingleSelect
          {...(props as SingleProps<T>)}
          disabled={disabled}
          hasContent={hasContent}
          labelId={labelId}
          onOpenChange={props.onOpenChange}
        />
      )}
    </InputWrapper>
  );
};
